import { applyEasing } from '../engine/animation/easing.js'
import {
  sampleAnimation,
  type AnimationSampleInput,
  type MutableAnimationSample,
} from '../engine/animation/sampleAnimation.js'
import {
  resolveAnimationTimelineWindows,
  sampleAnimationTimeline,
} from '../engine/animation/animationTimeline.js'
export {
  resolveAnimationSegmentWindows,
  resolveAnimationTimelineWindows,
  sampleAnimationSegment,
  sampleAnimationTimeline,
  type AnimationSegmentTimeline,
  type AnimationSegmentWindow,
  type AnimationTimelineSample,
} from '../engine/animation/animationTimeline.js'
import {
  validatePlaybackState,
  type MutablePlaybackState,
  type PlaybackState,
} from '../engine/animation/advancePlayback.js'
import {
  NO_PARTICLE_OWNER,
  ParticleBuffer,
  initializeParticleTargets,
} from '../engine/particles/ParticleBuffer.js'
import type { DeviceClass } from '../engine/particles/particleBudget.js'
import {
  validateLayoutResponse,
  type LayoutLetterMetadata,
  type LayoutResult,
  type LayoutSnapshot,
  type LayoutWordMetadata,
} from '../layout/LayoutProtocol.js'
import type { LayoutWorkerClient } from '../layout/LayoutWorkerClient.js'
import { effectiveLayoutFontSize } from '../layout/effectiveFontSize.js'
import { migrateProjectDocument } from '../project/migrateProjectDocument.js'
import {
  MAX_CANVAS_DIMENSION,
  MAX_CANVAS_PIXEL_AREA,
  MAX_LAYOUT_OFFSET_COUNT,
  MIN_CANVAS_DIMENSION,
  type Offset2D,
  type ProjectDocumentV2,
  type RendererPreference,
} from '../project/ProjectDocument.js'
import {
  type MutableRenderFrameView,
  type RenderBackend,
  type RenderBackendStatus,
  type RenderInvalidationReason,
} from '../render/RenderBackend.js'
import type { CreatedRenderBackend } from '../render/createRenderBackend.js'
import type {
  PortableOwnerSnapshot,
  PortableRuntimeSnapshot,
} from '../export/PortableProject.js'
import {
  createRenderScheduler,
  playbackSettingsFromProject,
  type AnimationFrameSource,
  type MonotonicClock,
  type RenderScheduler,
} from '../render/RenderScheduler.js'
import {
  createEditorStore,
  type EditorSelector,
  type EditorSnapshot,
  type EditorStore,
} from '../store/EditorStore.js'
import {
  EDITABLE_FIELD_PATHS,
  documentsSemanticallyEqual,
  type DetectedDotInput,
  type EditorCommand,
  type EditorViewport,
  type PlaybackFrame,
} from '../store/commands.js'

export type V2RuntimeBackendFactory = (
  preferred: RendererPreference,
  canvas2dPresentation: CreatedRenderBackend['canvas'] | null,
) => Promise<CreatedRenderBackend>

export interface V2RuntimeLayoutClient {
  layout(snapshot: LayoutSnapshot): Promise<LayoutResult>
  cancelCurrent(): void
  dispose(): void
}

export type V2RuntimeErrorPhase = 'backend' | 'layout' | 'prepare' | 'advance' | 'resize' | 'render'

export type LayoutOffsetTarget = 'word' | 'letter' | 'dot'

/**
 * Persisted layout offsets are index-keyed against the layout they were
 * authored on. Drag edits only ever pad an offsets array up to the dragged
 * index — which exists in the current layout — so an array that indexes past
 * the accepted layout's metadata counts can only be stale persisted state
 * (older text, older font, or an older layout algorithm). Left in place, those
 * entries displace arbitrary chunks of a layout they never belonged to.
 */
export function staleLayoutOffsetTargets(
  snapshot: Pick<LayoutSnapshot, 'wordOffsets' | 'letterOffsets' | 'dotOffsets'>,
  result: Pick<LayoutResult, 'words' | 'letters' | 'dots'>,
): readonly LayoutOffsetTarget[] {
  const stale: LayoutOffsetTarget[] = []
  if (snapshot.wordOffsets.length > result.words.length) stale.push('word')
  if (snapshot.letterOffsets.length > result.letters.length) stale.push('letter')
  if (snapshot.dotOffsets.length > result.dots.count) stale.push('dot')
  return stale
}

export interface V2RuntimeOptions {
  readonly initialProject: ProjectDocumentV2
  readonly layoutClient: V2RuntimeLayoutClient | LayoutWorkerClient
  readonly createBackend: V2RuntimeBackendFactory
  readonly raf: AnimationFrameSource
  readonly clock: MonotonicClock
  readonly deviceClass: DeviceClass
  readonly onError?: (error: unknown, phase: V2RuntimeErrorPhase) => void
  readonly onPlaybackComplete?: (progress: number) => void
  readonly resolveFontSource?: (fontName: string) => string | undefined
  /**
   * Notified when an accepted layout proves the requested offsets stale (see
   * staleLayoutOffsetTargets). The owner of the offset state should reset the
   * named targets; the resulting offset change re-requests the layout cleanly.
   */
  readonly onStaleLayoutOffsets?: (targets: readonly LayoutOffsetTarget[]) => void
}

export interface V2CanvasSnapshot {
  readonly revision: number
  readonly canvas: CreatedRenderBackend['canvas'] | null
  readonly kind: 'webgl2' | 'canvas2d' | null
}

export type V2PresentationCanvas = CreatedRenderBackend['canvas']

interface V2PresentationContext {
  clearRect(x: number, y: number, width: number, height: number): void
  drawImage(source: unknown, x: number, y: number, width: number, height: number): void
}

/**
 * Runtime-owned playback state for frame-rate consumers. This deliberately
 * lives outside EditorStore so animation does not invalidate the React tree.
 */
export interface V2PlaybackSnapshot {
  readonly revision: number
  readonly progress: number
  readonly isPlaying: boolean
  readonly direction: PlaybackState['travelDirection']
  readonly phase: PlaybackState['phase']
  readonly holdRemaining: number
  readonly completed: boolean
}

export interface V2LayoutSnapshot {
  readonly revision: number
  readonly words: readonly LayoutWordMetadata[]
  readonly letters: readonly LayoutLetterMetadata[]
  readonly dots: V2LayoutDotSnapshot
  readonly offsets: V2LayoutOffsetSnapshot
}

export interface V2LayoutOffsetSnapshot {
  readonly wordOffsets: ReadonlyArray<Readonly<Offset2D>>
  readonly letterOffsets: ReadonlyArray<Readonly<Offset2D>>
  readonly dotOffsets: ReadonlyArray<Readonly<Offset2D>>
}

export interface V2LayoutDotSnapshot {
  readonly count: number
  readonly x: V2ReadonlyNumericView
  readonly y: V2ReadonlyNumericView
  readonly width: V2ReadonlyNumericView
  readonly height: V2ReadonlyNumericView
  readonly relativeX: V2ReadonlyNumericView
  readonly relativeY: V2ReadonlyNumericView
  readonly wordIndex: V2ReadonlyNumericView
  readonly letterIndex: V2ReadonlyNumericView
}

export interface V2ReadonlyNumericView {
  readonly length: number
  at(index: number): number | undefined
}

export interface V2LayoutOffsetUpdate {
  readonly index: number
  readonly offset: Readonly<Offset2D>
}

export interface V2TransientState {
  readonly viewport: EditorViewport
  readonly playback: PlaybackFrame
  readonly renderingEnabled: boolean
  readonly selection: {
    readonly wordIndex: number | null
    readonly letterIndex: number | null
    readonly dotIndex: number | null
  }
}

const EMPTY_INDICES = new Uint32Array(0)
const EMPTY_LAYOUT_WORDS = Object.freeze([]) as readonly LayoutWordMetadata[]
const EMPTY_LAYOUT_LETTERS = Object.freeze([]) as readonly LayoutLetterMetadata[]
const EMPTY_LAYOUT_NUMBERS: V2ReadonlyNumericView = Object.freeze({
  length: 0,
  at: (_index: number) => undefined,
})
const EMPTY_LAYOUT_DOTS: V2LayoutDotSnapshot = Object.freeze({
  count: 0,
  x: EMPTY_LAYOUT_NUMBERS,
  y: EMPTY_LAYOUT_NUMBERS,
  width: EMPTY_LAYOUT_NUMBERS,
  height: EMPTY_LAYOUT_NUMBERS,
  relativeX: EMPTY_LAYOUT_NUMBERS,
  relativeY: EMPTY_LAYOUT_NUMBERS,
  wordIndex: EMPTY_LAYOUT_NUMBERS,
  letterIndex: EMPTY_LAYOUT_NUMBERS,
})
const EMPTY_LAYOUT_OFFSETS: V2LayoutOffsetSnapshot = Object.freeze({
  wordOffsets: Object.freeze([]),
  letterOffsets: Object.freeze([]),
  dotOffsets: Object.freeze([]),
})
const MAX_PROJECT_SYNC_PASSES = 8
const MAX_TRANSIENT_SYNC_PASSES = 8
const MAX_SELECTION_INDEX = 0xffff_fffe

type LayoutRequestStatus = 'pending' | 'accepted' | 'failed' | 'superseded' | 'disposed'

interface LayoutRequestRecord {
  status: LayoutRequestStatus
  readonly settled: Promise<void>
  settle(status: Exclude<LayoutRequestStatus, 'pending'>): void
}

interface LayoutEditSession {
  readonly revision: number
  dirty: boolean
  regenerateRequested: boolean
  readonly settled: Promise<void>
  settle(): void
}

function createLayoutRequestRecord(): LayoutRequestRecord {
  let resolve!: () => void
  const record: LayoutRequestRecord = {
    status: 'pending',
    settled: new Promise<void>((settle) => { resolve = settle }),
    settle(status) {
      if (record.status !== 'pending') return
      record.status = status
      resolve()
    },
  }
  return record
}

function createLayoutEditSession(revision: number): LayoutEditSession {
  let resolve!: () => void
  const session: LayoutEditSession = {
    revision,
    dirty: false,
    regenerateRequested: false,
    settled: new Promise<void>((settle) => { resolve = settle }),
    settle: resolve,
  }
  return session
}

export const selectV2CanvasWidth: EditorSelector<number> = (snapshot) => snapshot.document.canvas.width
export const selectV2CanvasHeight: EditorSelector<number> = (snapshot) => snapshot.document.canvas.height

function detectedDotsFromLayout(result: LayoutResult): readonly DetectedDotInput[] {
  const source = result.dots
  const count = source.count
  if (!Number.isSafeInteger(count) || count < 0 || count > MAX_LAYOUT_OFFSET_COUNT) {
    throw new RangeError('Runtime detected-dot count is outside the supported layout budget')
  }
  const dots = new Array<DetectedDotInput>(count)
  for (let index = 0; index < count; index += 1) {
    const x = source.x[index]
    const y = source.y[index]
    const width = source.width[index]
    const height = source.height[index]
    const relativeX = source.relativeX[index]
    const relativeY = source.relativeY[index]
    const wordIndex = source.wordIndex[index]
    const letterIndex = source.letterIndex[index]
    if (
      x === undefined || !Number.isFinite(x)
      || y === undefined || !Number.isFinite(y)
      || width === undefined || !Number.isFinite(width) || width < 0
      || height === undefined || !Number.isFinite(height) || height < 0
      || relativeX === undefined || !Number.isFinite(relativeX)
      || relativeY === undefined || !Number.isFinite(relativeY)
      || wordIndex === undefined || wordIndex >= 0xffff_ffff
      || letterIndex === undefined || letterIndex >= 0xffff_ffff
    ) throw new RangeError('Runtime detected-dot metadata is invalid')
    dots[index] = {
      x,
      y,
      width,
      height,
      relativeX,
      relativeY,
      wordIndex,
      letterIndex,
    }
  }
  return dots
}

function copyNumericView(source: Float32Array | Uint32Array): V2ReadonlyNumericView {
  const owned = source.slice()
  return Object.freeze({
    length: owned.length,
    at(index: number): number | undefined { return owned[index] },
  })
}

function copyPublishedMetadata(
  result: LayoutResult,
  request: LayoutSnapshot,
): Omit<V2LayoutSnapshot, 'revision'> {
  const words = Object.freeze(result.words.map((word) => Object.freeze({ ...word })))
  const letters = Object.freeze(result.letters.map((letter) => Object.freeze({ ...letter })))
  const dots = Object.freeze({
    count: result.dots.count,
    x: copyNumericView(result.dots.x),
    y: copyNumericView(result.dots.y),
    width: copyNumericView(result.dots.width),
    height: copyNumericView(result.dots.height),
    relativeX: copyNumericView(result.dots.relativeX),
    relativeY: copyNumericView(result.dots.relativeY),
    wordIndex: copyNumericView(result.dots.wordIndex),
    letterIndex: copyNumericView(result.dots.letterIndex),
  })
  const offsets = Object.freeze({
    wordOffsets: request.wordOffsets,
    letterOffsets: request.letterOffsets,
    dotOffsets: request.dotOffsets,
  })
  return Object.freeze({ words, letters, dots, offsets })
}

function projectFromSnapshot(snapshot: EditorSnapshot): ProjectDocumentV2 {
  return snapshot.document as unknown as ProjectDocumentV2
}

function initialPlayback(snapshot: EditorSnapshot): PlaybackState {
  const playback = snapshot.transient.playback
  const atEndpoint = playback.orderAmount === 0 || playback.orderAmount === 1
  const completed = !playback.isPlaying && playback.phase === 'complete' && atEndpoint
  return {
    progress: playback.orderAmount,
    travelDirection: playback.direction,
    phase: completed ? 'completed' : 'moving',
    holdRemaining: 0,
    completed,
  }
}

function playbackFromTransient(snapshot: EditorSnapshot): PlaybackState {
  const playback = snapshot.transient.playback
  const atEndpoint = playback.orderAmount === 0 || playback.orderAmount === 1
  const completed = !playback.isPlaying && playback.phase === 'complete' && atEndpoint
  return {
    progress: playback.orderAmount,
    travelDirection: playback.direction,
    phase: completed ? 'completed' : 'moving',
    holdRemaining: 0,
    completed,
  }
}

function copyPlaybackState(source: PlaybackState, target: MutablePlaybackState): void {
  target.progress = source.progress
  target.travelDirection = source.travelDirection
  target.phase = source.phase
  target.holdRemaining = source.holdRemaining
  target.completed = source.completed
}

function samePlaybackState(left: PlaybackState, right: PlaybackState): boolean {
  return Object.is(left.progress, right.progress)
    && left.travelDirection === right.travelDirection
    && left.phase === right.phase
    && Object.is(left.holdRemaining, right.holdRemaining)
    && left.completed === right.completed
}

function clonePlaybackState(source: PlaybackState): PlaybackState {
  return {
    progress: source.progress,
    travelDirection: source.travelDirection,
    phase: source.phase,
    holdRemaining: source.holdRemaining,
    completed: source.completed,
  }
}

function layoutChanged(before: ProjectDocumentV2, after: ProjectDocumentV2): boolean {
  return before.seed !== after.seed
    || before.text !== after.text
    || before.particles.density !== after.particles.density
    || before.appearance.gridMode !== after.appearance.gridMode
    || layoutOffsetsChanged(before, after)
    || before.canvas.width !== after.canvas.width
    || before.canvas.height !== after.canvas.height
}

function layoutOffsetsChanged(before: ProjectDocumentV2, after: ProjectDocumentV2): boolean {
  return before.layout.wordOffsets !== after.layout.wordOffsets
    || before.layout.letterOffsets !== after.layout.letterOffsets
    || before.layout.dotOffsets !== after.layout.dotOffsets
}

function nonOffsetLayoutInputChanged(before: ProjectDocumentV2, after: ProjectDocumentV2): boolean {
  return before.seed !== after.seed
    || before.text !== after.text
    || before.particles.density !== after.particles.density
    || before.appearance.gridMode !== after.appearance.gridMode
    || before.canvas.width !== after.canvas.width
    || before.canvas.height !== after.canvas.height
}

function hasWiggleMotion(project: Readonly<ProjectDocumentV2>): boolean {
  return project.animation.wiggleEnabled
    && project.animation.wiggleSpeed > 0
    && project.animation.wiggleForce > 0
    && project.animation.wiggleDampening > 0
}

function hasRotationMotion(project: Readonly<ProjectDocumentV2>): boolean {
  return project.animation.rotationEnabled && project.animation.rotationSpeed > 0
}

function isLivingAnimationMode(mode: string): boolean {
  return mode === 'breathing' || mode === 'shimmer' || mode === 'flow'
}

function hasLivingAnimationMotion(
  project: Readonly<ProjectDocumentV2>,
  playbackProgress: number,
): boolean {
  const animation = project.animation
  const eased = applyEasing(
    playbackProgress,
    animation.easingType,
    animation.easingAmount,
    animation.customEasingCurve,
  )
  if (Math.max(0, Math.min(1, eased)) === 0) return false
  const windows = resolveAnimationTimelineWindows(animation.type, animation.segments)
  if (windows === null) return isLivingAnimationMode(animation.type)
  const timeline = sampleAnimationTimeline(windows, eased)
  if (timeline.fromType === timeline.toType) return isLivingAnimationMode(timeline.fromType)
  return (timeline.blend < 1 && isLivingAnimationMode(timeline.fromType))
    || (timeline.blend > 0 && isLivingAnimationMode(timeline.toType))
}

function animationPathChanged(
  before: Readonly<ProjectDocumentV2>,
  after: Readonly<ProjectDocumentV2>,
): boolean {
  if (
    before.seed !== after.seed
    || before.canvas.width !== after.canvas.width
    || before.canvas.height !== after.canvas.height
  ) return true
  const left = before.animation
  const right = after.animation
  return left.type !== right.type
    || left.segments !== right.segments
    || left.easingType !== right.easingType
    || left.easingAmount !== right.easingAmount
    || left.customEasingCurve !== right.customEasingCurve
    || left.wiggleEnabled !== right.wiggleEnabled
    || left.wiggleSpeed !== right.wiggleSpeed
    || left.wiggleForce !== right.wiggleForce
    || left.wiggleDampening !== right.wiggleDampening
    || left.rotationEnabled !== right.rotationEnabled
    || left.rotationSpeed !== right.rotationSpeed
}

function playbackPositionDiscontinuous(before: PlaybackState, after: PlaybackState): boolean {
  const delta = after.progress - before.progress
  if (Math.abs(delta) > 0.25) return true
  if (before.travelDirection !== after.travelDirection) return false
  return (before.travelDirection === 1 && delta < 0)
    || (before.travelDirection === -1 && delta > 0)
}

function seededParticlePhase(seed: number, particleIndex: number): number {
  let value = (seed ^ Math.imul(particleIndex + 1, 0x9e37_79b9) ^ 0x85eb_ca6b) >>> 0
  value ^= value >>> 16
  value = Math.imul(value, 0x7feb_352d)
  value ^= value >>> 15
  value = Math.imul(value, 0x846c_a68b)
  value ^= value >>> 16
  return (value >>> 0) / 0x1_0000_0000 * Math.PI * 2
}

interface AnimationLayoutGeometry {
  readonly centerX: number
  readonly centerY: number
  readonly tangentX: Float32Array
  readonly tangentY: Float32Array
  readonly baseAlpha: Float32Array
}

function animationLayoutGeometry(
  particles: ParticleBuffer,
  wordCount: number,
  letterCount: number,
  fallbackCenterX: number,
  fallbackCenterY: number,
): AnimationLayoutGeometry {
  const count = particles.count
  const tangentX = new Float32Array(count)
  const tangentY = new Float32Array(count)
  tangentX.fill(-1)
  const baseAlpha = particles.alpha.slice(0, count)
  if (count === 0) {
    return { centerX: fallbackCenterX, centerY: fallbackCenterY, tangentX, tangentY, baseAlpha }
  }

  let minimumX = Number.POSITIVE_INFINITY
  let maximumX = Number.NEGATIVE_INFINITY
  let minimumY = Number.POSITIVE_INFINITY
  let maximumY = Number.NEGATIVE_INFINITY
  for (let index = 0; index < count; index += 1) {
    const x = particles.orderX[index] ?? 0
    const y = particles.orderY[index] ?? 0
    minimumX = Math.min(minimumX, x)
    maximumX = Math.max(maximumX, x)
    minimumY = Math.min(minimumY, y)
    maximumY = Math.max(maximumY, y)
  }
  const centerX = minimumX * 0.5 + maximumX * 0.5
  const centerY = minimumY * 0.5 + maximumY * 0.5

  const groupCount = letterCount + wordCount
  if (groupCount > 0) {
    const lastByGroup = new Int32Array(groupCount)
    lastByGroup.fill(-1)
    const sumX = new Float32Array(count)
    const sumY = new Float32Array(count)
    for (let index = 0; index < count; index += 1) {
      const letter = particles.letterIndex[index] ?? NO_PARTICLE_OWNER
      const word = particles.wordIndex[index] ?? NO_PARTICLE_OWNER
      const group = letter !== NO_PARTICLE_OWNER && letter < letterCount
        ? letter
        : word !== NO_PARTICLE_OWNER && word < wordCount
          ? letterCount + word
          : -1
      if (group < 0 || group >= groupCount) continue
      const previous = lastByGroup[group] ?? -1
      lastByGroup[group] = index
      if (previous < 0) continue
      const dx = (particles.orderX[index] ?? 0) - (particles.orderX[previous] ?? 0)
      const dy = (particles.orderY[index] ?? 0) - (particles.orderY[previous] ?? 0)
      const distance = Math.hypot(dx, dy)
      const localReach = Math.max(
        4,
        ((particles.size[index] ?? 1) + (particles.size[previous] ?? 1)) * 2.5,
      )
      if (distance <= 1e-6 || distance > localReach) continue
      const normalizedX = dx / distance
      const normalizedY = dy / distance
      sumX[index] = (sumX[index] ?? 0) + normalizedX
      sumY[index] = (sumY[index] ?? 0) + normalizedY
      sumX[previous] = (sumX[previous] ?? 0) + normalizedX
      sumY[previous] = (sumY[previous] ?? 0) + normalizedY
    }
    for (let index = 0; index < count; index += 1) {
      const x = sumX[index] ?? 0
      const y = sumY[index] ?? 0
      const length = Math.hypot(x, y)
      if (length <= 1e-6) continue
      tangentX[index] = x / length
      tangentY[index] = y / length
    }
  }

  return { centerX, centerY, tangentX, tangentY, baseAlpha }
}

function sameOffsets(
  left: readonly Readonly<{ x: number; y: number }>[],
  right: readonly Readonly<{ x: number; y: number }>[],
): boolean {
  if (left.length !== right.length) return false
  for (let index = 0; index < left.length; index += 1) {
    const a = left[index]
    const b = right[index]
    if (a === undefined || b === undefined || !Object.is(a.x, b.x) || !Object.is(a.y, b.y)) return false
  }
  return true
}

function fieldAt(document: ProjectDocumentV2, path: string): unknown {
  if (path === 'seed') return document.seed
  const separator = path.indexOf('.')
  const section = path.slice(0, separator) as keyof ProjectDocumentV2
  const field = path.slice(separator + 1)
  return (document[section] as unknown as Record<string, unknown>)[field]
}

function semanticValueEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true
  if (typeof left !== 'object' || left === null || typeof right !== 'object' || right === null) return false
  const leftRecord = left as Record<string, unknown>
  const rightRecord = right as Record<string, unknown>
  const leftKeys = Object.keys(leftRecord)
  if (leftKeys.length !== Object.keys(rightRecord).length) return false
  for (const key of leftKeys) {
    if (!semanticValueEqual(leftRecord[key], rightRecord[key])) return false
  }
  return true
}

function validateSurface(width: number, height: number, requestedDpr: number): void {
  if (
    !Number.isSafeInteger(width)
    || width < MIN_CANVAS_DIMENSION
    || width > MAX_CANVAS_DIMENSION
    || !Number.isSafeInteger(height)
    || height < MIN_CANVAS_DIMENSION
    || height > MAX_CANVAS_DIMENSION
    || width * height > MAX_CANVAS_PIXEL_AREA
  ) throw new RangeError('Runtime surface is outside the supported canvas budget')
  if (!Number.isFinite(requestedDpr) || requestedDpr <= 0) {
    throw new RangeError('Runtime surface DPR must be finite and positive')
  }
}

function finite(value: number, minimum: number, maximum: number, label: string): number {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be finite and within ${minimum}…${maximum}`)
  }
  return value
}

function assertBoolean(value: unknown, label: string): asserts value is boolean {
  if (typeof value !== 'boolean') throw new TypeError(`${label} must be boolean`)
}

function selectionIndex(value: number | null, label: string): number | null {
  if (value === null) return null
  if (!Number.isSafeInteger(value) || value < 0 || value > MAX_SELECTION_INDEX) {
    throw new RangeError(`${label} selection must be null or a non-negative Uint32 owner`)
  }
  return value
}

function normalizeTransient(transient: V2TransientState): V2TransientState {
  if (typeof transient !== 'object' || transient === null) {
    throw new TypeError('Transient state must be an object')
  }
  const viewport = transient.viewport
  const playback = transient.playback
  const selection = transient.selection
  if (typeof viewport !== 'object' || viewport === null) throw new TypeError('Viewport must be an object')
  if (typeof playback !== 'object' || playback === null) throw new TypeError('Playback must be an object')
  if (typeof selection !== 'object' || selection === null) throw new TypeError('Selection must be an object')

  const x = finite(viewport.x, -Number.MAX_VALUE, Number.MAX_VALUE, 'Viewport x')
  const y = finite(viewport.y, -Number.MAX_VALUE, Number.MAX_VALUE, 'Viewport y')
  const zoom = finite(viewport.zoom, Number.MIN_VALUE, Number.MAX_VALUE, 'Viewport zoom')
  const orderAmount = finite(playback.orderAmount, 0, 1, 'Playback order amount')
  if (typeof playback.isPlaying !== 'boolean') throw new TypeError('Playback playing state must be boolean')
  if (playback.direction !== 1 && playback.direction !== -1) {
    throw new RangeError('Playback direction must be 1 or -1')
  }
  if (
    playback.phase !== 'moving'
    && playback.phase !== 'holding-order'
    && playback.phase !== 'holding-chaos'
    && playback.phase !== 'complete'
  ) throw new RangeError('Playback phase is invalid')
  if (typeof transient.renderingEnabled !== 'boolean') {
    throw new TypeError('Rendering enabled state must be boolean')
  }

  return {
    viewport: { x, y, zoom },
    playback: {
      orderAmount,
      isPlaying: playback.isPlaying,
      direction: playback.direction,
      phase: playback.phase,
    },
    renderingEnabled: transient.renderingEnabled,
    selection: {
      wordIndex: selectionIndex(selection.wordIndex, 'Word'),
      letterIndex: selectionIndex(selection.letterIndex, 'Letter'),
      dotIndex: selectionIndex(selection.dotIndex, 'Dot'),
    },
  }
}

function transientMatches(snapshot: EditorSnapshot, candidate: V2TransientState): boolean {
  const current = snapshot.transient
  return Object.is(current.viewport.x, candidate.viewport.x)
    && Object.is(current.viewport.y, candidate.viewport.y)
    && Object.is(current.viewport.zoom, candidate.viewport.zoom)
    && Object.is(current.playback.orderAmount, candidate.playback.orderAmount)
    && current.playback.isPlaying === candidate.playback.isPlaying
    && current.playback.direction === candidate.playback.direction
    && current.playback.phase === candidate.playback.phase
    && current.renderingEnabled === candidate.renderingEnabled
    && current.selection.wordIndex === candidate.selection.wordIndex
    && current.selection.letterIndex === candidate.selection.letterIndex
    && current.selection.dotIndex === candidate.selection.dotIndex
}

function createFrame(
  project: ProjectDocumentV2,
  particles: ParticleBuffer,
  initialState: PlaybackState,
): MutableRenderFrameView {
  const playback: MutablePlaybackState = clonePlaybackState(initialState)
  return {
    frameNumber: 0,
    invalidationMask: 0,
    deltaSeconds: 0,
    timeSeconds: 0,
    project,
    playback,
    surface: {
      width: project.canvas.width,
      height: project.canvas.height,
      requestedDpr: 1,
      effectiveDpr: 1,
    },
    quality: { tier: 'high', particleBudget: 1 },
    viewport: { x: 0, y: 0, zoom: 1 },
    particles: {
      sourceCount: 0,
      renderCount: 0,
      indices: EMPTY_INDICES,
      positionX: particles.positionX,
      positionY: particles.positionY,
      previousPositionX: particles.previousPositionX,
      previousPositionY: particles.previousPositionY,
      size: particles.size,
      rotation: particles.rotation,
      alpha: particles.alpha,
    },
  }
}

export class V2Runtime {
  readonly store: EditorStore
  readonly particles = new ParticleBuffer()

  private readonly layoutClient: V2RuntimeLayoutClient
  private readonly createBackend: V2RuntimeBackendFactory
  private readonly raf: AnimationFrameSource
  private readonly clock: MonotonicClock
  private readonly deviceClass: DeviceClass
  private readonly onError: ((error: unknown, phase: V2RuntimeErrorPhase) => void) | null
  private readonly onPlaybackComplete: ((progress: number) => void) | null
  private readonly resolveFontSource: ((fontName: string) => string | undefined) | null
  private readonly onStaleLayoutOffsets: ((targets: readonly LayoutOffsetTarget[]) => void) | null
  private readonly frame: MutableRenderFrameView
  private readonly canvasListeners = new Set<() => void>()
  private readonly layoutListeners = new Set<() => void>()
  private readonly playbackListeners = new Set<() => void>()
  private readonly animationInput: AnimationSampleInput
  private readonly animationOutput: MutableAnimationSample = { x: 0, y: 0, progress: 0, alpha: 1 }
  private readonly animationBlendOutput: MutableAnimationSample = { x: 0, y: 0, progress: 0, alpha: 1 }

  private snapshot: EditorSnapshot
  private scheduler: RenderScheduler | null = null
  private activeHandle: CreatedRenderBackend | null = null
  private unsubscribeBackendStatus: (() => void) | null = null
  private unsubscribeStore: (() => void) | null = null
  private canvasSnapshot: V2CanvasSnapshot = Object.freeze({ revision: 0, canvas: null, kind: null })
  private layoutMetadataSnapshot: V2LayoutSnapshot = Object.freeze({
    revision: 0,
    words: EMPTY_LAYOUT_WORDS,
    letters: EMPTY_LAYOUT_LETTERS,
    dots: EMPTY_LAYOUT_DOTS,
    offsets: EMPTY_LAYOUT_OFFSETS,
  })
  private playbackSnapshot!: V2PlaybackSnapshot
  private startPromise: Promise<void> | null = null
  private started = false
  private disposed = false
  private synchronizingStore = false
  private currentLayoutRequest: LayoutRequestRecord | null = null
  private activeLayoutEdit: LayoutEditSession | null = null
  private backendGeneration = 0
  private pendingBackendGeneration: number | null = null
  private activeBackendGeneration = 0
  private deferredContextLossGeneration: number | null = null
  private pageVisible = true
  private reducedMotion = false
  private ambientMotion = false
  private playbackActive = false
  private requestedDpr = 1
  private completionNotified = false
  private playbackState: PlaybackState
  /**
   * Per-particle dot/diacritic ownership captured from the last accepted
   * layout (aligned with the live particle buffer). The particle buffer
   * itself only retains word/letter owners, so dot ownership is kept
   * alongside it for the animation kernel's ownerDot input.
   */
  private particleDotOwners: Uint32Array | null = null
  private particleBaseAlpha = new Float32Array(0)
  private particleTangentX = new Float32Array(0)
  private particleTangentY = new Float32Array(0)
  private layoutTextCenterX = 0
  private layoutTextCenterY = 0
  private motionHistoryValid = false
  private layoutWordCount = 0
  private layoutLetterCount = 0
  private presentationCanvas: V2PresentationCanvas | null = null
  private presentationContext: V2PresentationContext | null = null
  private presentationGeneration = 0

  constructor(options: V2RuntimeOptions) {
    if (options.onError !== undefined && typeof options.onError !== 'function') {
      throw new TypeError('Runtime error observer must be a function')
    }
    if (options.onPlaybackComplete !== undefined && typeof options.onPlaybackComplete !== 'function') {
      throw new TypeError('Runtime playback completion observer must be a function')
    }
    if (options.resolveFontSource !== undefined && typeof options.resolveFontSource !== 'function') {
      throw new TypeError('Runtime font source resolver must be a function')
    }
    if (options.onStaleLayoutOffsets !== undefined && typeof options.onStaleLayoutOffsets !== 'function') {
      throw new TypeError('Runtime stale-offset observer must be a function')
    }
    this.layoutClient = options.layoutClient
    this.createBackend = options.createBackend
    this.raf = options.raf
    this.clock = options.clock
    this.deviceClass = options.deviceClass
    this.onError = options.onError ?? null
    this.onPlaybackComplete = options.onPlaybackComplete ?? null
    this.resolveFontSource = options.resolveFontSource ?? null
    this.onStaleLayoutOffsets = options.onStaleLayoutOffsets ?? null
    this.store = createEditorStore(options.initialProject)
    this.snapshot = this.store.getSnapshot()
    this.playbackState = initialPlayback(this.snapshot)
    this.playbackSnapshot = Object.freeze({
      revision: 0,
      progress: this.playbackState.progress,
      isPlaying: this.snapshot.transient.playback.isPlaying && !this.playbackState.completed,
      direction: this.playbackState.travelDirection,
      phase: this.playbackState.phase,
      holdRemaining: this.playbackState.holdRemaining,
      completed: this.playbackState.completed,
    })
    this.frame = createFrame(projectFromSnapshot(this.snapshot), this.particles, this.playbackState)
    this.layoutTextCenterX = this.frame.project.canvas.width * 0.5
    this.layoutTextCenterY = this.frame.project.canvas.height * 0.5
    this.playbackActive = this.snapshot.transient.playback.isPlaying
      && !this.frame.playback.completed
    this.animationInput = {
      mode: this.frame.project.animation.type,
      easedProgress: 1,
      particleIndex: 0,
      particleCount: 1,
      orderX: 0,
      orderY: 0,
      chaosX: 0,
      chaosY: 0,
      seed: this.frame.project.seed,
      timeSeconds: 0,
      canvasWidth: this.frame.project.canvas.width,
      canvasHeight: this.frame.project.canvas.height,
      wiggleSpeed: this.frame.project.animation.wiggleSpeed,
      wiggleForce: this.frame.project.animation.wiggleForce,
      wiggleDampening: this.frame.project.animation.wiggleDampening,
      ownerWord: -1,
      ownerLetter: -1,
      ownerDot: -1,
      wordCount: 0,
      letterCount: 0,
    }
    this.unsubscribeStore = this.store.subscribe(() => {
      if (this.disposed || this.synchronizingStore) return
      this.reconcileSnapshot(this.store.getSnapshot())
    })
  }

  start(): Promise<void> {
    if (this.startPromise !== null) return this.startPromise
    if (this.disposed) return Promise.resolve()
    this.started = true
    const currentLayout = this.currentLayoutRequest
    const layout = currentLayout === null || currentLayout.status === 'failed'
      ? this.requestLayout()
      : currentLayout.settled
    this.startPromise = Promise.allSettled([
      layout,
      this.requestBackend(this.snapshot.document.canvas.preferredRenderer),
    ]).then(() => undefined)
    return this.startPromise
  }

  getCanvasSnapshot = (): V2CanvasSnapshot => this.canvasSnapshot

  getLayoutSnapshot = (): V2LayoutSnapshot => this.layoutMetadataSnapshot

  getPlaybackSnapshot = (): V2PlaybackSnapshot => this.playbackSnapshot

  subscribeCanvas = (listener: () => void): (() => void) => {
    if (typeof listener !== 'function') throw new TypeError('Canvas listener must be a function')
    if (this.disposed) return () => {}
    this.canvasListeners.add(listener)
    return () => { this.canvasListeners.delete(listener) }
  }

  attachPresentationCanvas(canvas: V2PresentationCanvas): () => void {
    if (this.disposed) return () => {}
    if (canvas === null || typeof canvas !== 'object') {
      throw new TypeError('Presentation canvas is required')
    }
    const context = canvas.getContext('2d', { alpha: true, desynchronized: true }) as Partial<V2PresentationContext> | null
    if (
      context === null
      || typeof context.clearRect !== 'function'
      || typeof context.drawImage !== 'function'
    ) throw new Error('Presentation Canvas2D context is unavailable')
    if (this.presentationGeneration >= Number.MAX_SAFE_INTEGER) {
      throw new RangeError('Presentation attachment generation exhausted the safe-integer range')
    }
    const generation = this.presentationGeneration + 1
    this.presentationGeneration = generation
    this.presentationCanvas = canvas
    this.presentationContext = context as V2PresentationContext
    this.scheduler?.invalidate('backend')
    return () => {
      if (generation !== this.presentationGeneration || this.presentationCanvas !== canvas) return
      this.presentationCanvas = null
      this.presentationContext = null
    }
  }

  subscribeLayout = (listener: () => void): (() => void) => {
    if (typeof listener !== 'function') throw new TypeError('Layout listener must be a function')
    if (this.disposed) return () => {}
    this.layoutListeners.add(listener)
    return () => { this.layoutListeners.delete(listener) }
  }

  subscribePlayback = (listener: () => void): (() => void) => {
    if (typeof listener !== 'function') throw new TypeError('Playback listener must be a function')
    if (this.disposed) return () => {}
    this.playbackListeners.add(listener)
    return () => { this.playbackListeners.delete(listener) }
  }

  syncProject(project: ProjectDocumentV2): boolean {
    if (this.disposed) return false
    if (this.synchronizingStore) throw new Error('Runtime store synchronization is already in progress')
    const candidate = migrateProjectDocument(project)
    this.synchronizingStore = true
    let changed = false
    let ownsTransaction = false
    let remainingPasses = MAX_PROJECT_SYNC_PASSES
    try {
      while (!documentsSemanticallyEqual(projectFromSnapshot(this.store.getSnapshot()), candidate)) {
        if (remainingPasses === 0) {
          throw new Error('Project synchronization did not converge under reentrant mutation')
        }
        this.store.beginTransaction('Sync legacy editor state')
        ownsTransaction = true
        while (!documentsSemanticallyEqual(projectFromSnapshot(this.store.getSnapshot()), candidate)) {
          if (remainingPasses === 0) {
            throw new Error('Project synchronization did not converge under reentrant mutation')
          }
          remainingPasses -= 1
          const currentDocument = projectFromSnapshot(this.store.getSnapshot())
          for (const path of EDITABLE_FIELD_PATHS) {
            const current = fieldAt(currentDocument, path)
            const next = fieldAt(candidate, path)
            if (!semanticValueEqual(current, next)) {
              changed = this.store.dispatch({
                type: 'document/set-field', path, value: next,
              } as EditorCommand) || changed
            }
          }
          if (
            currentDocument.canvas.width !== candidate.canvas.width
            || currentDocument.canvas.height !== candidate.canvas.height
            || currentDocument.canvas.preset !== candidate.canvas.preset
          ) {
            changed = this.store.dispatch({
              type: 'document/set-canvas',
              width: candidate.canvas.width,
              height: candidate.canvas.height,
              preset: candidate.canvas.preset,
            }) || changed
          }
          for (const target of ['word', 'letter', 'dot'] as const) {
            const key = `${target}Offsets` as const
            if (!sameOffsets(currentDocument.layout[key], candidate.layout[key])) {
              changed = this.store.dispatch({
                type: 'document/set-offsets', target, values: candidate.layout[key],
              }) || changed
            }
          }
        }
        this.store.commitTransaction()
        ownsTransaction = false
      }
    } catch (error) {
      if (ownsTransaction) this.store.cancelTransaction()
      throw error
    } finally {
      this.synchronizingStore = false
      const currentSnapshot = this.store.getSnapshot()
      if (currentSnapshot !== this.snapshot) this.reconcileSnapshot(currentSnapshot)
    }
    return changed
  }

  syncTransient(transient: V2TransientState): boolean {
    if (this.disposed) return false
    if (this.synchronizingStore) throw new Error('Runtime store synchronization is already in progress')
    const normalized = normalizeTransient(transient)
    let changed = false
    this.synchronizingStore = true
    try {
      for (let pass = 0; pass < MAX_TRANSIENT_SYNC_PASSES; pass += 1) {
        if (transientMatches(this.store.getSnapshot(), normalized)) return changed
        changed = this.store.dispatch({ type: 'transient/set-viewport', value: normalized.viewport }) || changed
        changed = this.store.dispatch({ type: 'transient/set-playback-frame', value: normalized.playback }) || changed
        changed = this.store.dispatch({
          type: 'transient/set-rendering-enabled', value: normalized.renderingEnabled,
        }) || changed
        for (const target of ['word', 'letter', 'dot'] as const) {
          changed = this.store.dispatch({
            type: 'transient/set-selection', target, index: normalized.selection[`${target}Index`],
          }) || changed
        }
      }
      if (!transientMatches(this.store.getSnapshot(), normalized)) {
        throw new Error('Transient synchronization did not converge under reentrant mutation')
      }
      return changed
    } finally {
      this.synchronizingStore = false
      const currentSnapshot = this.store.getSnapshot()
      if (currentSnapshot !== this.snapshot) this.reconcileSnapshot(currentSnapshot)
    }
  }

  setSurface(width: number, height: number, requestedDpr: number): boolean {
    if (this.disposed) return false
    validateSurface(width, height, requestedDpr)
    if (this.scheduler === null) {
      const changed = this.frame.surface.width !== width
        || this.frame.surface.height !== height
        || !Object.is(this.requestedDpr, requestedDpr)
      this.frame.surface.width = width
      this.frame.surface.height = height
      this.frame.surface.requestedDpr = requestedDpr
      this.requestedDpr = requestedDpr
      return changed
    }
    const changed = this.scheduler.setSurface(width, height, requestedDpr)
    this.requestedDpr = requestedDpr
    return changed
  }

  setPageVisible(visible: boolean): boolean {
    if (this.disposed) return false
    assertBoolean(visible, 'Page visibility')
    if (visible === this.pageVisible) return false
    this.pageVisible = visible
    return this.applyVisibility()
  }

  setReducedMotion(reduced: boolean): boolean {
    if (this.disposed) return false
    assertBoolean(reduced, 'Reduced motion')
    if (reduced === this.reducedMotion) return false
    this.reducedMotion = reduced
    return this.scheduler?.setReducedMotion(reduced) ?? true
  }

  setAmbientMotion(active: boolean): boolean {
    if (this.disposed) return false
    assertBoolean(active, 'Ambient motion')
    return this.applyAmbientMotion(active && this.ambientMotionEligible())
  }

  regenerateLayout(): Promise<void> {
    if (!this.started) return Promise.resolve()
    const edit = this.activeLayoutEdit
    if (edit !== null) {
      edit.regenerateRequested = true
      return edit.settled
    }
    return this.requestLayout()
  }

  beginLayoutEdit(revision: number): boolean {
    if (
      this.disposed
      || this.activeLayoutEdit !== null
      || !Number.isSafeInteger(revision)
      || revision !== this.layoutMetadataSnapshot.revision
      || this.currentLayoutRequest?.status !== 'accepted'
    ) return false
    this.store.beginTransaction('Edit layout offsets')
    this.activeLayoutEdit = createLayoutEditSession(revision)
    return true
  }

  updateLayoutEditOffsets(
    target: 'word' | 'letter' | 'dot',
    updates: readonly V2LayoutOffsetUpdate[],
  ): boolean {
    const edit = this.activeLayoutEdit
    if (
      this.disposed
      || edit === null
      || edit.revision !== this.layoutMetadataSnapshot.revision
      || updates.length === 0
    ) return false
    return this.store.dispatch({
      type: 'document/patch-offsets',
      target,
      updates: updates.map(({ index, offset }) => ({ index, value: offset })),
    })
  }

  commitLayoutEdit(revision: number): Promise<void> {
    const edit = this.activeLayoutEdit
    if (edit === null || edit.revision !== revision) return Promise.resolve()
    this.store.commitTransaction()
    this.activeLayoutEdit = null
    if (this.disposed || (!edit.dirty && !edit.regenerateRequested)) {
      edit.settle()
      return edit.settled
    }
    const request = this.requestLayout(true)
    void request.finally(() => { edit.settle() })
    return request
  }

  cancelLayoutEdit(revision: number): boolean {
    const edit = this.activeLayoutEdit
    if (edit === null || edit.revision !== revision) return false
    this.store.cancelTransaction()
    this.activeLayoutEdit = null
    edit.settle()
    return true
  }

  setPlaybackState(state: PlaybackState): boolean {
    if (this.disposed) return false
    validatePlaybackState(state)
    if (samePlaybackState(this.playbackState, state)) return false
    this.completionNotified = false
    this.motionHistoryValid = false
    this.playbackState = clonePlaybackState(state)
    if (this.scheduler === null) copyPlaybackState(state, this.frame.playback)
    else this.scheduler.setPlaybackState(state)
    this.playbackActive = this.snapshot.transient.playback.isPlaying && !state.completed
    this.publishPlayback()
    this.refreshAmbientMotion()
    return true
  }

  /** Seek is the only high-level operation allowed to replace engine progress. */
  seekPlayback(progress: number): boolean {
    if (this.disposed) return false
    finite(progress, 0, 1, 'Playback progress')
    return this.setPlaybackState({
      progress,
      travelDirection: this.playbackState.travelDirection,
      phase: 'moving',
      holdRemaining: 0,
      completed: false,
    })
  }

  /** Toggle play intent without replacing progress, holds, or travel direction. */
  setPlaying(playing: boolean): boolean {
    if (this.disposed) return false
    assertBoolean(playing, 'Playback playing state')
    return this.store.dispatch({ type: 'transient/set-playing', value: playing })
  }

  async createPortableSnapshot(): Promise<PortableRuntimeSnapshot> {
    if (this.disposed) throw new Error('Cannot export a disposed V2 runtime')
    if (this.currentLayoutRequest === null) void this.requestLayout()

    for (let attempt = 0; attempt < MAX_PROJECT_SYNC_PASSES; attempt += 1) {
      if (this.disposed) throw new Error('Cannot export a disposed V2 runtime')
      const request = this.currentLayoutRequest
      if (request === null) continue
      await request.settled
      if (this.disposed) throw new Error('Cannot export a disposed V2 runtime')
      if (request !== this.currentLayoutRequest) continue
      if (request.status !== 'accepted') {
        throw new Error('The current V2 layout is unavailable for export')
      }

      const snapshot = this.snapshot
      const count = this.particles.count
      const values = new Float32Array(count * 3)
      for (let index = 0; index < count; index += 1) {
        const offset = index * 3
        values[offset] = this.particles.orderX[index] ?? 0
        values[offset + 1] = this.particles.orderY[index] ?? 0
        values[offset + 2] = this.particles.size[index] ?? 0
      }
      // Owner-driven animation modes (pen/words/dots scheduling) need the
      // layout ownership captured with the live particle buffer so the export
      // renders identically to the editor. The letter→word and dot→word/letter
      // maps let the portable runtime rebuild a validation-consistent layout.
      const layoutMetadata = this.layoutMetadataSnapshot
      let owners: PortableOwnerSnapshot | undefined
      if (
        layoutMetadata.words.length > 0
        || layoutMetadata.letters.length > 0
        || layoutMetadata.dots.count > 0
      ) {
        const dotIndex = new Uint32Array(count)
        const particleDotOwners = this.particleDotOwners
        if (particleDotOwners === null || particleDotOwners.length < count) {
          dotIndex.fill(NO_PARTICLE_OWNER)
        } else {
          dotIndex.set(particleDotOwners.subarray(0, count))
        }
        const letterWord = new Uint32Array(layoutMetadata.letters.length)
        for (let index = 0; index < layoutMetadata.letters.length; index += 1) {
          letterWord[index] = layoutMetadata.letters[index]?.wordIndex ?? NO_PARTICLE_OWNER
        }
        const dotCount = layoutMetadata.dots.count
        const dotWord = new Uint32Array(dotCount)
        const dotLetter = new Uint32Array(dotCount)
        for (let index = 0; index < dotCount; index += 1) {
          dotWord[index] = layoutMetadata.dots.wordIndex.at(index) ?? NO_PARTICLE_OWNER
          dotLetter[index] = layoutMetadata.dots.letterIndex.at(index) ?? NO_PARTICLE_OWNER
        }
        owners = {
          wordCount: layoutMetadata.words.length,
          letterCount: layoutMetadata.letters.length,
          dotCount,
          wordIndex: this.particles.wordIndex.slice(0, count),
          letterIndex: this.particles.letterIndex.slice(0, count),
          dotIndex,
          letterWord,
          dotWord,
          dotLetter,
        }
      }
      if (
        this.disposed
        || request !== this.currentLayoutRequest
        || request.status !== 'accepted'
        || snapshot !== this.snapshot
      ) continue

      const playback = this.playbackState
      const document = migrateProjectDocument(projectFromSnapshot(snapshot))
      // A looping composition is never "completed" — normalize the terminal
      // paused state so the export is valid with the default loop settings.
      const looping = document.animation.loopMode !== 'once'
      const completed = playback.completed && !looping
      const terminalPhase = playback.phase === 'completed'
      return {
        document,
        session: {
          progress: playback.progress,
          autoplay: snapshot.transient.playback.isPlaying && !completed,
          travelDirection: playback.travelDirection,
          phase: looping && terminalPhase ? 'moving' : playback.phase,
          holdRemaining: looping && terminalPhase ? 0 : playback.holdRemaining,
          completed,
          reducedMotion: this.reducedMotion,
        },
        targets: { count, stride: 3, values, ...(owners === undefined ? {} : { owners }) },
      }
    }
    throw new Error('V2 layout changed continuously during export')
  }

  invalidate(reason: RenderInvalidationReason): boolean {
    return this.scheduler?.invalidate(reason) ?? false
  }

  dispose(): boolean {
    if (this.disposed) return false
    const edit = this.activeLayoutEdit
    if (edit !== null) {
      this.store.cancelTransaction()
      this.activeLayoutEdit = null
      edit.settle()
    }
    this.disposed = true
    this.currentLayoutRequest?.settle('disposed')
    if (this.backendGeneration < Number.MAX_SAFE_INTEGER) this.backendGeneration += 1
    this.pendingBackendGeneration = null
    this.deferredContextLossGeneration = null
    const unsubscribeStore = this.unsubscribeStore
    this.unsubscribeStore = null
    try { unsubscribeStore?.() } catch (error) { this.report(error, 'backend') }
    const unsubscribeBackendStatus = this.unsubscribeBackendStatus
    this.unsubscribeBackendStatus = null
    try { unsubscribeBackendStatus?.() } catch (error) { this.report(error, 'backend') }
    try { this.layoutClient.dispose() } catch (error) { this.report(error, 'layout') }
    const scheduler = this.scheduler
    this.scheduler = null
    const activeHandle = this.activeHandle
    this.activeHandle = null
    this.presentationCanvas = null
    this.presentationContext = null
    if (scheduler !== null) {
      try { scheduler.dispose() } catch (error) { this.report(error, 'backend') }
    } else if (activeHandle !== null) this.disposeCreatedBackend(activeHandle)
    if (this.canvasSnapshot.canvas !== null) this.publishCanvas(null)
    this.publishLayout(null)
    this.canvasListeners.clear()
    this.layoutListeners.clear()
    this.playbackListeners.clear()
    return true
  }

  private report(error: unknown, phase: V2RuntimeErrorPhase): void {
    try { this.onError?.(error, phase) } catch { /* Observers cannot affect ownership. */ }
  }

  private reconcileSnapshot(next: EditorSnapshot): void {
    const previous = this.snapshot
    if (next === previous) return
    this.snapshot = next
    const beforeDocument = projectFromSnapshot(previous)
    const nextDocument = projectFromSnapshot(next)
    this.frame.project = nextDocument

    if (beforeDocument !== nextDocument) {
      this.scheduler?.invalidate('project')
      if (animationPathChanged(beforeDocument, nextDocument)) this.motionHistoryValid = false
      if (
        this.currentLayoutRequest !== null
        && layoutChanged(beforeDocument, nextDocument)
      ) {
        const edit = this.activeLayoutEdit
        if (
          layoutOffsetsChanged(beforeDocument, nextDocument)
          && !nonOffsetLayoutInputChanged(beforeDocument, nextDocument)
        ) {
          // Offset-only changes (drag patches or slider ticks) leave the raw
          // word/letter/dot geometry untouched; the worker only re-bakes
          // offset-dependent targets. Keep the accepted overlay metadata and
          // detected dots published while that request is in flight so rapid
          // ticks cannot blank the outline.
          if (edit !== null) edit.dirty = true
          else void this.requestLayout(true)
        } else void this.requestLayout()
      }
      if (beforeDocument.animation !== nextDocument.animation) {
        this.scheduler?.setPlaybackSettings(playbackSettingsFromProject(nextDocument))
        if (beforeDocument.animation.loopMode !== nextDocument.animation.loopMode) {
          this.completionNotified = false
        }
        this.refreshAmbientMotion()
      }
      if (
        beforeDocument.canvas.width !== nextDocument.canvas.width
        || beforeDocument.canvas.height !== nextDocument.canvas.height
      ) {
        this.setSurface(nextDocument.canvas.width, nextDocument.canvas.height, this.requestedDpr)
      }
      if (this.started && beforeDocument.canvas.preferredRenderer !== nextDocument.canvas.preferredRenderer) {
        void this.requestBackend(nextDocument.canvas.preferredRenderer)
      }
    }

    const beforeTransient = previous.transient
    const nextTransient = next.transient
    if (beforeTransient.viewport !== nextTransient.viewport) {
      this.frame.viewport.x = nextTransient.viewport.x
      this.frame.viewport.y = nextTransient.viewport.y
      this.frame.viewport.zoom = nextTransient.viewport.zoom
      this.scheduler?.invalidate('viewport')
    }
    if (beforeTransient.selection !== nextTransient.selection) this.scheduler?.invalidate('selection')
    if (beforeTransient.playback !== nextTransient.playback) {
      if (
        !Object.is(beforeTransient.playback.orderAmount, nextTransient.playback.orderAmount)
        || beforeTransient.playback.direction !== nextTransient.playback.direction
        || beforeTransient.playback.phase !== nextTransient.playback.phase
      ) {
        this.completionNotified = false
        this.motionHistoryValid = false
        const state = playbackFromTransient(next)
        this.playbackState = clonePlaybackState(state)
        if (this.scheduler === null) copyPlaybackState(state, this.frame.playback)
        else this.scheduler.setPlaybackState(state)
      }
      if (beforeTransient.playback.isPlaying !== nextTransient.playback.isPlaying) {
        if (nextTransient.playback.isPlaying) {
          this.completionNotified = false
          if (this.playbackState.completed) {
            const state: PlaybackState = {
              progress: this.playbackState.progress,
              travelDirection: nextTransient.playback.direction,
              phase: 'moving',
              holdRemaining: 0,
              completed: false,
            }
            this.playbackState = clonePlaybackState(state)
            if (this.scheduler === null) copyPlaybackState(state, this.frame.playback)
            else this.scheduler.setPlaybackState(state)
          }
          this.scheduler?.play()
        } else this.scheduler?.pause()
      }
      this.playbackActive = nextTransient.playback.isPlaying && !this.frame.playback.completed
      this.publishPlayback()
      this.refreshAmbientMotion()
    }
    if (beforeTransient.renderingEnabled !== nextTransient.renderingEnabled) {
      this.refreshAmbientMotion()
      this.applyVisibility()
    }
  }

  private applyVisibility(): boolean {
    return this.scheduler?.setVisible(this.pageVisible && this.snapshot.transient.renderingEnabled) ?? true
  }

  private layoutSnapshot(): LayoutSnapshot {
    const project = projectFromSnapshot(this.snapshot)
    const fontSource = this.resolveFontSource?.(project.text.activeFont)
    return {
      seed: project.seed,
      text: project.text.content,
      fontName: project.text.activeFont,
      ...(fontSource === undefined ? {} : { fontSource }),
      fontSize: effectiveLayoutFontSize(
        project.text.content,
        project.text.fontSize,
        project.canvas.width,
        project.canvas.height,
      ),
      lineHeight: project.text.lineHeight,
      wordSpacing: project.text.wordSpacing,
      textAlign: project.text.textAlign,
      baseline: project.text.baseline,
      density: project.particles.density,
      gridMode: project.appearance.gridMode,
      width: project.canvas.width,
      height: project.canvas.height,
      particleBudget: this.particles.maxCapacity,
      wordOffsets: project.layout.wordOffsets,
      letterOffsets: project.layout.letterOffsets,
      dotOffsets: project.layout.dotOffsets,
    }
  }

  private requestLayout(preserveAcceptedMetadata = false): Promise<void> {
    if (this.disposed) return Promise.resolve()
    const previous = this.currentLayoutRequest
    const record = createLayoutRequestRecord()
    this.currentLayoutRequest = record
    previous?.settle('superseded')
    if (!preserveAcceptedMetadata) this.publishLayout(null)
    if (this.disposed) {
      record.settle('disposed')
      return record.settled
    }
    if (record !== this.currentLayoutRequest) {
      record.settle('superseded')
      return record.settled
    }
    if (!preserveAcceptedMetadata) {
      try {
        this.store.dispatch({ type: 'transient/set-detected-dots', value: [] })
      } catch (error) {
        record.settle('failed')
        this.report(error, 'layout')
        return record.settled
      }
    }
    if (this.disposed) {
      record.settle('disposed')
      return record.settled
    }
    if (record !== this.currentLayoutRequest) {
      record.settle('superseded')
      return record.settled
    }
    let request: Promise<LayoutResult>
    let requestedSnapshot: LayoutSnapshot
    try {
      requestedSnapshot = this.layoutSnapshot()
      request = this.layoutClient.layout(requestedSnapshot)
    } catch (error) {
      record.settle('failed')
      this.report(error, 'layout')
      return record.settled
    }
    void Promise.resolve(request).then((result) => {
      if (this.disposed) {
        record.settle('disposed')
        return
      }
      if (record !== this.currentLayoutRequest) {
        record.settle('superseded')
        return
      }
      try {
        validateLayoutResponse({ type: 'layout-result', revision: 0, result }, this.particles.maxCapacity)
        const detectedDots = detectedDotsFromLayout(result)
        const project = projectFromSnapshot(this.snapshot)
        initializeParticleTargets(this.particles, result.targets, {
          seed: project.seed,
          chaosRadius: Math.hypot(project.canvas.width, project.canvas.height),
          canvasWidth: project.canvas.width,
          canvasHeight: project.canvas.height,
        })
        // Captured with the live buffer so owner metadata can never drift
        // from the particles it describes; the copy keeps runtime ownership.
        this.particleDotOwners = result.targets.dotIndex.slice()
        this.layoutWordCount = result.words.length
        this.layoutLetterCount = result.letters.length
        const animationGeometry = animationLayoutGeometry(
          this.particles,
          this.layoutWordCount,
          this.layoutLetterCount,
          project.canvas.width * 0.5,
          project.canvas.height * 0.5,
        )
        this.particleBaseAlpha = animationGeometry.baseAlpha
        this.particleTangentX = animationGeometry.tangentX
        this.particleTangentY = animationGeometry.tangentY
        this.layoutTextCenterX = animationGeometry.centerX
        this.layoutTextCenterY = animationGeometry.centerY
        const frameParticles = this.frame.particles
        frameParticles.sourceCount = this.particles.count
        frameParticles.positionX = this.particles.positionX
        frameParticles.positionY = this.particles.positionY
        frameParticles.previousPositionX = this.particles.previousPositionX
        frameParticles.previousPositionY = this.particles.previousPositionY
        frameParticles.size = this.particles.size
        frameParticles.rotation = this.particles.rotation
        frameParticles.alpha = this.particles.alpha
        // A regenerated layout replaces target identity and may move every
        // particle discontinuously. The first prepared frame must therefore
        // start with zero velocity instead of blurring from the old layout.
        this.motionHistoryValid = false
        this.store.dispatch({
          type: 'transient/set-detected-dots',
          value: detectedDots,
        })
        if (this.disposed) {
          record.settle('disposed')
          return
        }
        if (record !== this.currentLayoutRequest) {
          record.settle('superseded')
          return
        }
        this.publishLayout(result, requestedSnapshot)
        if (this.disposed) {
          record.settle('disposed')
          return
        }
        if (record !== this.currentLayoutRequest) {
          record.settle('superseded')
          return
        }
        this.refreshAmbientMotion()
        this.scheduler?.invalidate('layout')
        record.settle('accepted')
        const staleOffsetTargets = staleLayoutOffsetTargets(requestedSnapshot, result)
        if (staleOffsetTargets.length > 0 && this.onStaleLayoutOffsets !== null) {
          try {
            this.onStaleLayoutOffsets(staleOffsetTargets)
          } catch { /* Observers cannot affect layout ownership. */ }
        }
      } catch (error) {
        record.settle('failed')
        this.report(error, 'layout')
      }
    }, (error: unknown) => {
      if (this.disposed) record.settle('disposed')
      else if (record !== this.currentLayoutRequest) record.settle('superseded')
      else {
        record.settle('failed')
        this.report(error, 'layout')
      }
    })
    return record.settled
  }

  private requestBackend(preferred: RendererPreference): Promise<void> {
    if (this.disposed || !this.started) return Promise.resolve()
    const generation = this.beginBackendOperation()
    let request: Promise<CreatedRenderBackend>
    try {
      request = this.createBackend(preferred, this.presentationCanvas)
    } catch (error) {
      this.report(error, 'backend')
      this.settleFailedBackendOperation(generation)
      return Promise.resolve()
    }
    return request.then((handle) => {
      if (!this.isCurrentBackendOperation(generation)) {
        this.disposeCreatedBackend(handle)
        return
      }
      try {
        this.installBackend(handle, generation)
        this.settleInstalledBackendOperation(generation)
      } catch (error) {
        this.report(error, 'backend')
        this.settleFailedBackendOperation(generation)
      }
    }, (error: unknown) => {
      if (this.isCurrentBackendOperation(generation)) {
        this.report(error, 'backend')
        this.settleFailedBackendOperation(generation)
      }
    })
  }

  private beginBackendOperation(): number {
    if (this.backendGeneration >= Number.MAX_SAFE_INTEGER) {
      throw new RangeError('Runtime backend generation exhausted the safe-integer range')
    }
    const generation = this.backendGeneration + 1
    this.backendGeneration = generation
    this.pendingBackendGeneration = generation
    return generation
  }

  private disposeCreatedBackend(handle: CreatedRenderBackend): void {
    try { handle.backend.dispose() } catch (error) { this.report(error, 'backend') }
  }

  private isCurrentBackendOperation(generation: number): boolean {
    return !this.disposed
      && generation === this.backendGeneration
      && generation === this.pendingBackendGeneration
  }

  private settleInstalledBackendOperation(generation: number): void {
    if (!this.isCurrentBackendOperation(generation)) return
    this.pendingBackendGeneration = null
    this.resumeDeferredContextLoss()
  }

  private settleFailedBackendOperation(generation: number): void {
    if (!this.isCurrentBackendOperation(generation)) return
    this.pendingBackendGeneration = null
    this.resumeDeferredContextLoss()
  }

  private resumeDeferredContextLoss(): void {
    const handle = this.activeHandle
    const generation = this.activeBackendGeneration
    if (
      handle === null
      || this.deferredContextLossGeneration !== generation
      || this.pendingBackendGeneration !== null
    ) return
    this.deferredContextLossGeneration = null
    this.handleBackendStatus(handle, generation, 'context-lost')
  }

  private installBackend(handle: CreatedRenderBackend, generation: number): void {
    const previousUnsubscribe = this.unsubscribeBackendStatus
    let committed = false
    let pendingStatus: RenderBackendStatus | null = null
    let candidateUnsubscribe: (() => void) | null = null
    let candidateDisposed = false
    try {
      candidateUnsubscribe = handle.backend.subscribeStatus((status) => {
        if (!committed) {
          pendingStatus = status
          return
        }
        this.handleBackendStatus(handle, generation, status)
      })
      if (typeof candidateUnsubscribe !== 'function') {
        throw new TypeError('Runtime backend status subscription must return an unsubscribe function')
      }

      if (this.scheduler === null) {
        let scheduler: RenderScheduler | null = null
        try {
          scheduler = createRenderScheduler({
            clock: this.clock,
            raf: this.raf,
            backend: handle.backend,
            frameView: this.frame,
            prepareFrame: (frame) => { this.prepareFrame(frame) },
            initialPlayback: this.playbackState,
            playbackSettings: playbackSettingsFromProject(projectFromSnapshot(this.snapshot)),
            deviceClass: this.deviceClass,
            onError: (error, phase) => { this.report(error, phase) },
            onRenderCommitted: (backend) => { this.presentFrame(backend) },
            onPlaybackChange: (state) => { this.handlePlaybackChange(state) },
          })
          scheduler.setReducedMotion(this.reducedMotion)
          this.refreshAmbientMotion()
          scheduler.setAmbientMotion(this.ambientMotion)
          scheduler.setVisible(this.pageVisible && this.snapshot.transient.renderingEnabled)
          if (this.snapshot.transient.playback.isPlaying) scheduler.play()
          scheduler.invalidate('backend')
          if (this.particles.count > 0) scheduler.invalidate('layout')
        } catch (error) {
          candidateDisposed = true
          if (scheduler === null) this.disposeCreatedBackend(handle)
          else {
            try { scheduler.dispose() } catch (disposeError) { this.report(disposeError, 'backend') }
          }
          throw error
        }
        if (this.disposed) {
          try { candidateUnsubscribe() } catch { /* Terminal cleanup is best-effort. */ }
          try { scheduler.dispose() } catch (error) { this.report(error, 'backend') }
          return
        }
        this.scheduler = scheduler
      } else {
        try {
          const adoption = this.scheduler.adoptBackend(handle.backend, this.deviceClass)
          if (adoption === 'unchanged') {
            try { candidateUnsubscribe() } catch { /* Same-backend candidate cleanup is best-effort. */ }
            return
          }
          if (adoption === 'unadopted') {
            try { candidateUnsubscribe() } catch { /* Unadopted candidate cleanup is best-effort. */ }
            candidateDisposed = true
            this.disposeCreatedBackend(handle)
            return
          }
        } catch (error) {
          candidateDisposed = true
          this.disposeCreatedBackend(handle)
          throw error
        }
        if (this.disposed) {
          try { candidateUnsubscribe() } catch { /* Terminal cleanup is best-effort. */ }
          return
        }
      }
    } catch (error) {
      try { candidateUnsubscribe?.() } catch { /* Candidate observation cleanup is best-effort. */ }
      if (!candidateDisposed) this.disposeCreatedBackend(handle)
      throw error
    }

    try { previousUnsubscribe?.() } catch (error) { this.report(error, 'backend') }
    if (this.disposed) {
      try { candidateUnsubscribe() } catch { /* Terminal cleanup is best-effort. */ }
      return
    }
    this.unsubscribeBackendStatus = candidateUnsubscribe
    this.deferredContextLossGeneration = null
    this.activeHandle = handle
    this.activeBackendGeneration = generation
    committed = true
    let status: RenderBackendStatus | null = pendingStatus as RenderBackendStatus | null
    if (status === null) {
      try { status = handle.backend.getStatus() } catch (error) { this.report(error, 'backend') }
    }
    if (status !== null) {
      try { this.handleBackendStatus(handle, generation, status) } catch (error) { this.report(error, 'backend') }
    }
    if (
      !this.disposed
      && this.activeHandle === handle
      && this.activeBackendGeneration === generation
    ) this.publishCanvas(handle)
  }

  private handlePlaybackChange(state: PlaybackState): void {
    if (this.disposed) return
    if (playbackPositionDiscontinuous(this.playbackState, state)) this.motionHistoryValid = false
    this.playbackState = clonePlaybackState(state)
    copyPlaybackState(state, this.frame.playback)
    this.playbackActive = this.snapshot.transient.playback.isPlaying && !state.completed
    this.publishPlayback()
    this.refreshAmbientMotion()
    if (
      !state.completed
      || this.frame.project.animation.loopMode !== 'once'
      || this.completionNotified
    ) return
    this.completionNotified = true
    try { this.onPlaybackComplete?.(state.progress) } catch { /* Legacy completion cannot fault rendering. */ }
  }

  private publishPlayback(): boolean {
    const current = this.playbackSnapshot
    const state = this.playbackState
    const isPlaying = this.snapshot.transient.playback.isPlaying && !state.completed
    if (
      Object.is(current.progress, state.progress)
      && current.isPlaying === isPlaying
      && current.direction === state.travelDirection
      && current.phase === state.phase
      && Object.is(current.holdRemaining, state.holdRemaining)
      && current.completed === state.completed
    ) return false
    const next = Object.freeze({
      revision: current.revision + 1,
      progress: state.progress,
      isPlaying,
      direction: state.travelDirection,
      phase: state.phase,
      holdRemaining: state.holdRemaining,
      completed: state.completed,
    })
    this.playbackSnapshot = next
    // Unlike low-frequency canvas/layout publication, avoid a cohort-array
    // allocation on every animation frame. Set iteration is deletion-safe.
    for (const listener of this.playbackListeners) {
      try { listener() } catch { /* Playback observers cannot fault scheduler advance. */ }
    }
    return true
  }

  private ambientMotionEligible(): boolean {
    if (
      this.particles.count === 0
      || !this.snapshot.transient.renderingEnabled
      || this.playbackActive
    ) return false
    const project = this.frame.project
    if (hasRotationMotion(project)) return true
    const progress = this.frame.playback.progress
    if (hasLivingAnimationMotion(project, progress)) return true
    return progress > 0 && progress < 1 && hasWiggleMotion(project)
  }

  private applyAmbientMotion(active: boolean): boolean {
    if (active === this.ambientMotion) return false
    this.ambientMotion = active
    return this.scheduler?.setAmbientMotion(active) ?? true
  }

  private refreshAmbientMotion(): boolean {
    return this.applyAmbientMotion(this.ambientMotionEligible())
  }

  private handleBackendStatus(
    handle: CreatedRenderBackend,
    generation: number,
    status: RenderBackendStatus,
  ): void {
    if (
      this.disposed
      || handle !== this.activeHandle
      || generation !== this.activeBackendGeneration
    ) return
    if (status !== 'context-lost') {
      if (status === 'ready' && this.deferredContextLossGeneration === generation) {
        this.deferredContextLossGeneration = null
      }
      return
    }
    if (this.pendingBackendGeneration !== null) {
      this.deferredContextLossGeneration = generation
      return
    }
    const fallbackGeneration = this.beginBackendOperation()
    let fallback: CreatedRenderBackend
    try {
      fallback = handle.createCanvasFallback()
    } catch (error) {
      this.report(error, 'backend')
      this.settleFailedBackendOperation(fallbackGeneration)
      return
    }
    if (!this.isCurrentBackendOperation(fallbackGeneration)) {
      this.disposeCreatedBackend(fallback)
      return
    }
    try {
      this.installBackend(fallback, fallbackGeneration)
      this.settleInstalledBackendOperation(fallbackGeneration)
    } catch (error) {
      this.report(error, 'backend')
      this.settleFailedBackendOperation(fallbackGeneration)
    }
  }

  private publishCanvas(handle: CreatedRenderBackend | null): void {
    this.canvasSnapshot = Object.freeze({
      revision: this.canvasSnapshot.revision + 1,
      canvas: handle?.canvas ?? null,
      kind: handle?.backend.kind ?? null,
    })
    for (const listener of [...this.canvasListeners]) {
      try { listener() } catch { /* Canvas observers cannot corrupt runtime ownership. */ }
    }
  }

  private presentFrame(renderedBackend: RenderBackend): void {
    const destination = this.presentationCanvas
    const context = this.presentationContext
    const activeHandle = this.activeHandle
    if (activeHandle?.backend !== renderedBackend) return
    const source = activeHandle.canvas
    if (destination === null || context === null || source === undefined) return
    if (source === destination) return
    const width = source.width
    const height = source.height
    if (!Number.isSafeInteger(width) || width <= 0 || !Number.isSafeInteger(height) || height <= 0) {
      throw new RangeError('Backend canvas dimensions are invalid for presentation')
    }
    if (destination.width !== width) destination.width = width
    if (destination.height !== height) destination.height = height
    context.clearRect(0, 0, width, height)
    context.drawImage(source, 0, 0, width, height)
  }

  private publishLayout(result: LayoutResult | null, request?: LayoutSnapshot): void {
    if (
      result === null
      && this.layoutMetadataSnapshot.words === EMPTY_LAYOUT_WORDS
      && this.layoutMetadataSnapshot.letters === EMPTY_LAYOUT_LETTERS
      && this.layoutMetadataSnapshot.dots === EMPTY_LAYOUT_DOTS
    ) return
    const metadata = result === null
      ? {
        words: EMPTY_LAYOUT_WORDS,
        letters: EMPTY_LAYOUT_LETTERS,
        dots: EMPTY_LAYOUT_DOTS,
        offsets: EMPTY_LAYOUT_OFFSETS,
      }
      : copyPublishedMetadata(result, request ?? this.layoutSnapshot())
    this.layoutMetadataSnapshot = Object.freeze({
      revision: this.layoutMetadataSnapshot.revision + 1,
      ...metadata,
    })
    for (const listener of [...this.layoutListeners]) {
      try { listener() } catch { /* Layout observers cannot corrupt runtime ownership. */ }
    }
  }

  private prepareFrame(frame: MutableRenderFrameView): void {
    const project = frame.project
    const count = this.particles.count
    if (count === 0) return
    const animation = project.animation
    const eased = applyEasing(
      frame.playback.progress,
      animation.easingType,
      animation.easingAmount,
      animation.customEasingCurve,
    )
    // The main selector is a fixed first timeline phase. Later modes are
    // sampled at the same global progress and crossfaded, so changing modes
    // never restarts particles from chaos or snaps at a segment boundary.
    const windows = resolveAnimationTimelineWindows(animation.type, animation.segments)
    const timeline = windows === null ? null : sampleAnimationTimeline(windows, eased)
    const input = this.animationInput
    input.easedProgress = timeline?.progress ?? eased
    input.particleCount = count
    input.seed = project.seed
    input.timeSeconds = frame.timeSeconds
    input.canvasWidth = project.canvas.width
    input.canvasHeight = project.canvas.height
    input.wiggleSpeed = animation.wiggleSpeed
    input.wiggleForce = animation.wiggleEnabled
      ? animation.wiggleForce
      : 0
    input.wiggleDampening = animation.wiggleDampening
    input.wordCount = this.layoutWordCount
    input.letterCount = this.layoutLetterCount
    input.textCenterX = this.layoutTextCenterX
    input.textCenterY = this.layoutTextCenterY
    const rotationTime = animation.rotationEnabled
      ? frame.timeSeconds * animation.rotationSpeed
      : 0
    const wordOwners = this.particles.wordIndex
    const letterOwners = this.particles.letterIndex
    const dotOwners = this.particleDotOwners
    const retainMotionHistory = this.motionHistoryValid && frame.deltaSeconds > 0
    for (let index = 0; index < count; index += 1) {
      const wordOwner = wordOwners[index] ?? NO_PARTICLE_OWNER
      const letterOwner = letterOwners[index] ?? NO_PARTICLE_OWNER
      const dotOwner = dotOwners?.[index] ?? NO_PARTICLE_OWNER
      input.particleIndex = index
      input.orderX = this.particles.orderX[index] ?? 0
      input.orderY = this.particles.orderY[index] ?? 0
      input.chaosX = this.particles.chaosX[index] ?? 0
      input.chaosY = this.particles.chaosY[index] ?? 0
      input.ownerWord = wordOwner === NO_PARTICLE_OWNER ? -1 : wordOwner
      input.ownerLetter = letterOwner === NO_PARTICLE_OWNER ? -1 : letterOwner
      input.ownerDot = dotOwner === NO_PARTICLE_OWNER ? -1 : dotOwner
      input.tangentX = this.particleTangentX[index] ?? -1
      input.tangentY = this.particleTangentY[index] ?? 0
      const previousX = this.particles.positionX[index] ?? 0
      const previousY = this.particles.positionY[index] ?? 0
      input.mode = timeline?.fromType ?? animation.type
      sampleAnimation(input, this.animationOutput)
      let x = this.animationOutput.x
      let y = this.animationOutput.y
      let progress = this.animationOutput.progress
      let alpha = this.animationOutput.alpha ?? 1
      if (timeline !== null && timeline.blend > 0 && timeline.fromType !== timeline.toType) {
        input.mode = timeline.toType
        sampleAnimation(input, this.animationBlendOutput)
        x += (this.animationBlendOutput.x - x) * timeline.blend
        y += (this.animationBlendOutput.y - y) * timeline.blend
        progress += (this.animationBlendOutput.progress - progress) * timeline.blend
        alpha += ((this.animationBlendOutput.alpha ?? 1) - alpha) * timeline.blend
      }
      this.animationOutput.x = x
      this.animationOutput.y = y
      this.animationOutput.progress = progress
      this.animationOutput.alpha = alpha
      this.particles.positionX[index] = x
      this.particles.positionY[index] = y
      this.particles.alpha[index] = (this.particleBaseAlpha[index] ?? 1) * alpha
      this.particles.previousPositionX[index] = retainMotionHistory ? previousX : x
      this.particles.previousPositionY[index] = retainMotionHistory ? previousY : y
      this.particles.rotation[index] = animation.rotationEnabled
        ? rotationTime + seededParticlePhase(project.seed, index)
        : 0
    }
    this.motionHistoryValid = true
  }
}

export function createV2Runtime(options: V2RuntimeOptions): V2Runtime {
  return new V2Runtime(options)
}
